import React, { useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { User, Linkedin, Github, Globe, Clock, Save, Sparkles, CheckCircle, Wrench, Loader2, Briefcase, GraduationCap } from "lucide-react";
import { SkillSelector } from "@/components/profile/SkillSelector";
import { ToolSelector } from "@/components/profile/ToolSelector";
import { InterestsPicker } from "@/components/profile/InterestsPicker";
import { useAutoSave } from "@/hooks/useAutoSave";
import { AutoSaveIndicator } from "@/components/admin/AutoSaveIndicator";

interface ContributorProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  bio: string | null;
  phone: string | null;
  availability_hours_per_week: number | null;
  interests: string[];
  linkedin_url: string | null;
  github_url: string | null;
  portfolio_url: string | null;
  timezone: string | null;
  completed_onboarding: boolean;
  open_to_networking: boolean;
}

const AVAILABILITY_OPTIONS = [
  { value: "1", label: "1-2 hours/week" },
  { value: "5", label: "3-5 hours/week" },
  { value: "10", label: "5-10 hours/week" },
  { value: "20", label: "10-20 hours/week" },
  { value: "40", label: "20+ hours/week" },
];

const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
  "Europe/London",
  "Europe/Paris",
  "Asia/Tokyo",
  "Australia/Sydney",
];

const UserProfile: React.FC = () => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Partial<ContributorProfile>>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Keep formData in a ref to avoid stale closure issues in saveProfile
  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Fetch user and profile data
  const { data: userData } = useQuery({
    queryKey: ["current-user"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  const { data: profile, isLoading } = useQuery({
    queryKey: ["contributor-profile", userData?.id],
    queryFn: async () => {
      if (!userData?.id) return null;
      
      const { data, error } = await supabase
        .from("contributor_profiles")
        .select("*")
        .eq("id", userData.id)
        .maybeSingle();
      
      if (error) throw error;
      return data as ContributorProfile | null;
    },
    enabled: !!userData?.id,
  });

  // Fetch user skills (current)
  const { data: currentSkills } = useQuery({
    queryKey: ["contributor-skills-current", userData?.id],
    queryFn: async () => {
      if (!userData?.id) return [];
      
      const { data, error } = await supabase
        .from("contributor_skills")
        .select(`
          id,
          proficiency,
          years_experience,
          skill_tag_id,
          skill_type,
          skill_tags (id, name, category)
        `)
        .eq("profile_id", userData.id)
        .eq("skill_type", "current");
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!userData?.id,
  });

  // Fetch user skills (learning)
  const { data: learningSkills } = useQuery({
    queryKey: ["contributor-skills-learning", userData?.id],
    queryFn: async () => {
      if (!userData?.id) return [];
      
      const { data, error } = await supabase
        .from("contributor_skills")
        .select(`
          id,
          proficiency,
          years_experience,
          skill_tag_id,
          skill_type,
          skill_tags (id, name, category)
        `)
        .eq("profile_id", userData.id)
        .eq("skill_type", "learning");
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!userData?.id,
  });

  // Fetch user tools
  const { data: tools } = useQuery({
    queryKey: ["contributor-tools", userData?.id],
    queryFn: async () => {
      if (!userData?.id) return [];
      
      const { data, error } = await supabase
        .from("contributor_tools")
        .select(`
          id,
          proficiency,
          tool_tag_id,
          tool_tags (id, name, category, is_custom, library_resource_id)
        `)
        .eq("profile_id", userData.id);
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!userData?.id,
  });

  // Save profile function - uses ref to always get latest formData
  const saveProfile = useCallback(async () => {
    if (!userData?.id) throw new Error("Not authenticated");

    // Use ref to get latest formData - avoids stale closure
    const currentFormData = formDataRef.current;

    console.log("[saveProfile] Saving profile for user:", userData.id);
    console.log("[saveProfile] Current form data:", currentFormData);

    // Explicitly map all fields to ensure proper data structure
    const profileData = {
      id: userData.id,
      first_name: currentFormData.first_name || null,
      last_name: currentFormData.last_name || null,
      display_name: currentFormData.display_name || null,
      bio: currentFormData.bio || null,
      phone: currentFormData.phone || null,
      availability_hours_per_week: currentFormData.availability_hours_per_week || null,
      interests: currentFormData.interests || [],
      linkedin_url: currentFormData.linkedin_url || null,
      github_url: currentFormData.github_url || null,
      portfolio_url: currentFormData.portfolio_url || null,
      timezone: currentFormData.timezone || null,
      completed_onboarding: currentFormData.completed_onboarding || false,
      open_to_networking: currentFormData.open_to_networking || false,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from("contributor_profiles")
      .upsert(profileData, { onConflict: 'id' })
      .select();

    console.log("[saveProfile] Result:", { data, error });

    if (error) throw error;
  }, [userData?.id]); // formData removed - accessed via ref

  // Auto-save hook
  const { status: saveStatus, markDirty, markClean, lastSavedAt } = useAutoSave({
    onSave: saveProfile,
    debounceMs: 1500,
    enabled: !!userData?.id && isInitialized,
  });

  // Initialize form data
  useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name,
        last_name: profile.last_name,
        display_name: profile.display_name,
        bio: profile.bio,
        phone: profile.phone,
        availability_hours_per_week: profile.availability_hours_per_week,
        interests: profile.interests || [],
        linkedin_url: profile.linkedin_url,
        github_url: profile.github_url,
        portfolio_url: profile.portfolio_url,
        timezone: profile.timezone,
        completed_onboarding: profile.completed_onboarding,
        open_to_networking: profile.open_to_networking || false,
      });
      // Mark clean after loading profile data
      setTimeout(() => {
        setIsInitialized(true);
        markClean();
      }, 100);
    } else if (userData?.email && !profile) {
      // Auto-populate display name from email for new profiles
      const emailPrefix = userData.email.split("@")[0];
      const displayName = emailPrefix
        .split(/[._-]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      setFormData({ display_name: displayName, interests: [], open_to_networking: false });
      setTimeout(() => {
        setIsInitialized(true);
        markClean();
      }, 100);
    }
  }, [profile, userData?.email, markClean]);

  // Manual save mutation (for Mark Complete button)
  const saveMutation = useMutation({
    mutationFn: async (data: Partial<ContributorProfile>) => {
      if (!userData?.id) throw new Error("Not authenticated");

      const profileData = {
        id: userData.id,
        ...data,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from("contributor_profiles")
        .upsert(profileData, { onConflict: 'id' });

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Profile saved successfully");
      queryClient.invalidateQueries({ queryKey: ["contributor-profile"] });
    },
    onError: (error) => {
      toast.error(`Failed to save profile: ${error.message}`);
    },
  });

  const handleChange = (field: keyof ContributorProfile, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    markDirty();
  };

  // Direct field save on blur - bypasses auto-save hook for reliable link saving
  const saveField = async (field: keyof ContributorProfile, value: string | null) => {
    console.log(`[saveField] Called for ${field} with value:`, value);
    
    if (!userData?.id) {
      console.log("[saveField] No userData.id - aborting");
      return;
    }
    
    try {
      console.log(`[saveField] Upserting ${field} for user ${userData.id}...`);
      const { error } = await supabase
        .from("contributor_profiles")
        .upsert(
          { id: userData.id, [field]: value || null, updated_at: new Date().toISOString() },
          { onConflict: 'id' }
        );
      
      if (error) throw error;
      console.log(`[saveField] Success for ${field}`);
      toast.success(`${field.replace(/_url$/, "").replace(/_/g, " ")} saved`);
    } catch (error) {
      console.error(`[saveField] Failed to save ${field}:`, error);
      toast.error(`Failed to save ${field.replace(/_url$/, "").replace(/_/g, " ")}`);
    }
  };

  // Save all link fields at once
  const saveAllFields = async () => {
    console.log("[saveAllFields] Button clicked");
    console.log("[saveAllFields] userData?.id:", userData?.id);
    console.log("[saveAllFields] Current formData:", formData);
    
    if (!userData?.id) {
      console.log("[saveAllFields] No user ID - aborting");
      toast.error("Not authenticated");
      return;
    }
    
    setIsSaving(true);
    
    const payload = {
      id: userData.id,
      linkedin_url: formData.linkedin_url || null,
      github_url: formData.github_url || null,
      portfolio_url: formData.portfolio_url || null,
      timezone: formData.timezone || null,
      updated_at: new Date().toISOString(),
    };
    
    console.log("[saveAllFields] Payload to upsert:", payload);
    
    try {
      console.log("[saveAllFields] Starting Supabase upsert...");
      const { data, error } = await supabase
        .from("contributor_profiles")
        .upsert(payload, { onConflict: 'id' })
        .select();
      
      console.log("[saveAllFields] Supabase response:", { data, error });
      
      if (error) throw error;
      toast.success("Profile saved successfully");
    } catch (error) {
      console.error("[saveAllFields] Error:", error);
      toast.error("Failed to save profile");
    } finally {
      setIsSaving(false);
    }
  };

  const handleMarkComplete = () => {
    saveMutation.mutate({ ...formData, completed_onboarding: true });
  };

  // Calculate profile completeness
  const calculateCompleteness = () => {
    let score = 0;
    const weights = {
      display_name: 15,
      bio: 15,
      skills: 20,
      tools: 10,
      interests: 15,
      availability: 10,
      timezone: 5,
      links: 10,
    };

    if (formData.display_name) score += weights.display_name;
    if (formData.bio && formData.bio.length > 20) score += weights.bio;
    if ((currentSkills && currentSkills.length > 0) || (learningSkills && learningSkills.length > 0)) score += weights.skills;
    if (tools && tools.length > 0) score += weights.tools;
    if (formData.interests && formData.interests.length > 0) score += weights.interests;
    if (formData.availability_hours_per_week) score += weights.availability;
    if (formData.timezone) score += weights.timezone;
    if (formData.linkedin_url || formData.github_url || formData.portfolio_url) score += weights.links;

    return score;
  };

  const completeness = calculateCompleteness();

  if (isLoading) {
    return (
      <div className="p-6 md:p-8 animate-pulse">
        <div className="h-8 w-48 bg-muted rounded mb-4" />
        <div className="h-64 bg-muted rounded" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">My Profile</h1>
          <p className="text-muted-foreground mt-1">
            Complete your profile to get matched with relevant challenges and tasks.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <AutoSaveIndicator status={saveStatus} lastSavedAt={lastSavedAt} />
        </div>
      </div>

      {/* Profile Completeness */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <span className="font-medium">Profile Completeness</span>
            </div>
            <span className="text-sm font-medium">{completeness}%</span>
          </div>
          <Progress value={completeness} className="h-2" />
          {completeness < 100 && (
            <p className="text-sm text-muted-foreground mt-2">
              Complete your profile to unlock personalized task recommendations.
            </p>
          )}
          {completeness >= 80 && !formData.completed_onboarding && (
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-3"
              onClick={handleMarkComplete}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Mark Profile as Complete
            </Button>
          )}
        </CardContent>
      </Card>

      <div className="space-y-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Basic Information
            </CardTitle>
            <CardDescription>
              Your public profile information visible to team members.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={formData.first_name || ""}
                  onChange={(e) => handleChange("first_name", e.target.value)}
                  placeholder="Your first name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={formData.last_name || ""}
                  onChange={(e) => handleChange("last_name", e.target.value)}
                  placeholder="Your last name"
                />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="display_name">Display Name</Label>
                <Input
                  id="display_name"
                  value={formData.display_name || ""}
                  onChange={(e) => handleChange("display_name", e.target.value)}
                  placeholder="How should we call you?"
                />
                <p className="text-xs text-muted-foreground">
                  Shown publicly on teams and challenges
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  value={userData?.email || ""}
                  disabled
                  className="bg-muted"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone || ""}
                onChange={(e) => handleChange("phone", e.target.value)}
                placeholder="+1 (555) 123-4567"
              />
              <p className="text-xs text-muted-foreground">
                Optional. Used for team coordination.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                value={formData.bio || ""}
                onChange={(e) => handleChange("bio", e.target.value)}
                placeholder="Tell us a bit about yourself, your background, and what you're passionate about..."
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                {formData.bio?.length || 0}/500 characters
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Skills I Have */}
        <Card id="skills">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Skills I Have
            </CardTitle>
            <CardDescription>
              Add skills you're confident in. Teams will match you to relevant tasks based on your proficiency.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SkillSelector 
              userId={userData?.id || ""} 
              skills={currentSkills || []}
              onUpdate={() => queryClient.invalidateQueries({ queryKey: ["contributor-skills-current"] })}
              skillType="current"
            />
          </CardContent>
        </Card>

        {/* Skills I Want to Learn */}
        <Card id="skills-learning">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5" />
              Skills I Want to Learn
            </CardTitle>
            <CardDescription>
              Tell us what you're curious about. We'll feature skill-building opportunities at future events.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SkillSelector 
              userId={userData?.id || ""} 
              skills={learningSkills || []}
              onUpdate={() => queryClient.invalidateQueries({ queryKey: ["contributor-skills-learning"] })}
              skillType="learning"
            />
          </CardContent>
        </Card>

        {/* Tools */}
        <Card id="tools">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Tools Experience
            </CardTitle>
            <CardDescription>
              Add the tools and platforms you're familiar with. This helps match you with projects that use these tools.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ToolSelector 
              userId={userData?.id || ""} 
              tools={tools || []}
              onUpdate={() => queryClient.invalidateQueries({ queryKey: ["contributor-tools"] })}
            />
          </CardContent>
        </Card>

        {/* Interests */}
        <Card>
          <CardHeader>
            <CardTitle>Interests</CardTitle>
            <CardDescription>
              We'll use this to connect you with relevant challenges and introduce you to like-minded people.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <InterestsPicker
              selected={formData.interests || []}
              onChange={(interests) => handleChange("interests", interests)}
              userId={userData?.id}
              openToNetworking={formData.open_to_networking || false}
              onNetworkingChange={(value) => handleChange("open_to_networking", value)}
            />
          </CardContent>
        </Card>

        {/* Availability */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Availability
            </CardTitle>
            <CardDescription>
              Let teams know how much time you can contribute.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="availability">Hours per Week</Label>
                <Select
                  value={formData.availability_hours_per_week?.toString() || ""}
                  onValueChange={(value) => handleChange("availability_hours_per_week", parseInt(value))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select availability" />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABILITY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="timezone">Timezone</Label>
                <Select
                  value={formData.timezone || ""}
                  onValueChange={(value) => {
                    setFormData(prev => ({ ...prev, timezone: value }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map(tz => (
                      <SelectItem key={tz} value={tz}>
                        {tz.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Links */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Links & Social
            </CardTitle>
            <CardDescription>
              Connect your professional profiles (optional).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="linkedin" className="flex items-center gap-2">
                <Linkedin className="h-4 w-4" /> LinkedIn
              </Label>
              <Input
                id="linkedin"
                value={formData.linkedin_url || ""}
                onChange={(e) => setFormData(prev => ({ ...prev, linkedin_url: e.target.value }))}
                placeholder="https://linkedin.com/in/yourprofile"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="github" className="flex items-center gap-2">
                <Github className="h-4 w-4" /> GitHub
              </Label>
              <Input
                id="github"
                value={formData.github_url || ""}
                onChange={(e) => setFormData(prev => ({ ...prev, github_url: e.target.value }))}
                placeholder="https://github.com/yourusername"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="portfolio" className="flex items-center gap-2">
                <Globe className="h-4 w-4" /> Portfolio / Website
              </Label>
              <Input
                id="portfolio"
                value={formData.portfolio_url || ""}
                onChange={(e) => setFormData(prev => ({ ...prev, portfolio_url: e.target.value }))}
                placeholder="https://yourwebsite.com"
              />
            </div>
            <Button 
              onClick={saveAllFields} 
              variant="outline" 
              className="w-full mt-4"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save All Changes
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserProfile;
